/* eslint-disable */
import { detailMetricScales } from '../utils/detailMetricScales';
/**
 * Construcción del modelo de presentación para comparar días.
 *
 * Los métodos operan sobre la instancia de PanelLogic y no mantienen estado global.
 */
const logic = {
  cmpVals(st) {
    const D = this.data,
      ci = st.sel,
      N = D.days.length;
    const mode = st.cMode || 'sup',
      selM = st.cMetrics || ['act', 'tasa', 'ppa'];
    const HEX = {
      act: [79, 209, 160],
      tasa: [227, 154, 79],
      ppa: [232, 86, 80],
      oxy: [111, 182, 230],
      temp: [224, 127, 176],
      cur: [168, 140, 240],
      dep: [94, 196, 196]
    };
    const MD = {
      act: {
        l: 'Actividad',
        u: '%',
        g: p => p.act,
        lo: 0,
        hi: 100,
        dp: 0,
        c: 'oklch(0.78 0.14 160)'
      },
      tasa: {
        l: 'Tasa',
        u: 'kg/min',
        g: p => p.tasa,
        lo: 0,
        dp: 0,
        c: 'oklch(0.72 0.14 55)'
      },
      ppa: {
        l: 'PPA acum.',
        u: 'pellets',
        g: p => p.ppaC,
        lo: 0,
        dp: 0,
        c: 'oklch(0.68 0.2 25)'
      },
      oxy: {
        l: 'Oxígeno',
        u: '%',
        g: p => p.oxy,
        dp: 1,
        c: 'oklch(0.78 0.11 220)'
      },
      temp: {
        l: 'Temperatura',
        u: '°C',
        g: p => p.temp,
        dp: 2,
        c: 'oklch(0.74 0.13 340)'
      },
      cur: {
        l: 'Corriente',
        u: 'cm/s',
        g: p => p.cur,
        lo: 0,
        dp: 1,
        c: 'oklch(0.72 0.14 295)'
      },
      dep: {
        l: 'Profundidad',
        u: 'm',
        g: p => p.dep,
        lo: 0,
        dp: 1,
        c: 'oklch(0.75 0.09 190)'
      }
    };
    const pal = ['oklch(0.78 0.12 250)', 'oklch(0.84 0.14 90)', 'oklch(0.78 0.14 160)', 'oklch(0.72 0.16 320)', 'oklch(0.72 0.17 25)', 'oklch(0.8 0.1 200)'];
    const hm = m => String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(Math.round(m % 60)).padStart(2, '0');
    const fast = d => D.rows[ci].cells[d].ay;
    let cDays = st.cDays;
    if (!cDays) {
      cDays = [];
      [st.day, st.day - 1, st.day - 7].forEach(d => {
        if (d >= 0 && !fast(d) && !cDays.includes(d)) cDays.push(d);
      });
      if (!cDays.length) cDays = [st.day];
    }
    // The three comparison modes share the selection established by the
    // superposed view. Canvas-based modes sort the same days for their axis.
    const range = cDays.slice().sort((a, b) => a - b);
    const used = mode === 'sup' ? cDays : range;
    // Resolve the complete V1 presentation series directly from the selected
    // cage so detail, superposition, heat map and 3D share the same points.
    const v1Series = (d, metric) => {
      const node = D.rows[ci]?.node || {},
        day = D.days[d],
        dayKey = value => {
          const parts = String(value || '').split(/[\/-]/);
          if (parts.length !== 3) return String(value || '');
          const ordered = parts[0].length === 4 ? [parts[2], parts[1], parts[0]] : parts;
          return ordered.map((part, index) => index < 2 ? String(part).padStart(2, '0') : part).join('/');
        },
        entries = source => Array.isArray(source) ? source : Array.isArray(source?.data) ? source.data : [],
        onDay = source => entries(source).find(entry => dayKey(entry?.day) === dayKey(day))?.value || [],
        minute = value => {
          const match = String(value || '').match(/(?:T|\s|^)(\d{1,2}):(\d{2})(?::(\d{2}))?/);
          return match ? Number(match[1]) * 60 + Number(match[2]) + Number(match[3] || 0) / 60 : NaN;
        },
        pointMetric = (point, key) => {
          const source = point?.synthetic && point?.tooltipSource ? point.tooltipSource : point;
          return Number(source?.snapshot?.[key] ?? source?.[key]);
        },
        activities = Object.entries(node).filter(([key]) => key.startsWith('graph_activity')).map(([, source]) => onDay(source)),
        from = (points, getter) => (Array.isArray(points) ? points : []).flatMap(point => {
          const t = minute(point?.time),
            value = Number(getter(point));
          return Number.isFinite(t) && Number.isFinite(value) ? [{
            t,
            value
          }] : [];
        }).sort((a, b) => a.t - b.t);
      if (metric === 'act') return from(activities[0], point => point.value);
      if (metric === 'tasa') {
        const preferred = this.props.selectedRateKey === 'tasa_pfm' ? node.graph_feeding_pfm : node.graph_feeding;
        const points = onDay(preferred);
        return from(points.length ? points : onDay(node.graph_feeding).length ? onDay(node.graph_feeding) : onDay(node.tasa_data), point => point.value);
      }
      if (metric === 'ppa') return from(onDay(node.graph_perdidos), point => point.value);
      const key = {
        oxy: ['so2', 'o2'],
        temp: ['temp'],
        cur: ['cs'],
        dep: ['depth']
      }[metric] || [];
      const source = activities.find(points => points.some(point => key.some(candidate => Number.isFinite(pointMetric(point, candidate))))) || [];
      return from(source, point => {
        const candidate = key.find(name => Number.isFinite(pointMetric(point, name)));
        return candidate ? pointMetric(point, candidate) : NaN;
      });
    };
    const detailDays = mode === '3d' ? used.map(day => this.detail(ci, day)) : null;
    const rawByMetric = selM.map(k => used.map((d, index) => detailDays
      ? detailDays[index].series[k === 'ppa' ? 'ppaC' : k] || [] : v1Series(d, k)));
    const measuredTimes = rawByMetric.flat(2).map(sample => sample.t).filter(Number.isFinite);
    const cmpStart = measuredTimes.length ? Math.min(...measuredTimes) : 450;
    const cmpEndRaw = measuredTimes.length ? Math.max(...measuredTimes) : 1350;
    const cmpEnd = Math.max(cmpStart + 5, cmpEndRaw);
    const slotCount = Math.max(2, Math.floor((cmpEnd - cmpStart) / 5) + 1);
    const cmpHours = [...Array(7)].map((_, j) => ({
      f: j / 6,
      lab: hm(cmpStart + (cmpEnd - cmpStart) * j / 6)
    }));
    const slotSeries = (samples, metric) => {
      const groups = Array.from({
        length: slotCount
      }, () => []);
      samples.forEach(sample => {
        const index = Math.max(0, Math.min(slotCount - 1, Math.round((sample.t - cmpStart) / 5)));
        groups[index].push(sample.value);
      });
      const values = groups.map(group => group.length ? metric === 'ppa' ? Math.max(...group) : group.reduce((sum, value) => sum + value, 0) / group.length : NaN);
      let previous = -1;
      values.forEach((value, index) => {
        if (!Number.isFinite(value)) return;
        if (previous >= 0 && index - previous > 1) {
          const start = values[previous];
          for (let gap = previous + 1; gap < index; gap++) {
            values[gap] = metric === 'tasa' ? start : start + (value - start) * (gap - previous) / (index - previous);
          }
        }
        previous = index;
      });
      return values;
    };
    const bounds = (md, arr) => {
      let lo = md.lo,
        hi = md.hi;
      const all = arr.flat().filter(Number.isFinite);
      if (lo == null) lo = all.length ? Math.min(...all) : 0;
      if (hi == null) hi = all.length ? Math.max(...all) : lo + 1;
      if (md.lo == null) lo -= (hi - lo) * 0.06 || 1;
      if (md.hi == null) hi += (hi - lo) * 0.06 || 1;
      return [lo, hi];
    };
    const curvePath = (samples, metric, X, Y) => {
      const points = samples.map(sample => ({
        x: Number(X(sample.t)),
        y: Number(Y(sample.value))
      }));
      if (!points.length) return 'M0 0';
      if (metric === 'tasa') return points.reduce((path, point, index) => index ? path + ' H' + point.x.toFixed(1) + ' V' + point.y.toFixed(2) : 'M' + point.x.toFixed(1) + ' ' + point.y.toFixed(2), '');
      if (points.length < 3) return points.map((point, index) => (index ? 'L' : 'M') + point.x.toFixed(1) + ' ' + point.y.toFixed(2)).join(' ');
      const tension = 0.35;
      const controls = points.map((point, index) => {
        const previous = points[Math.max(0, index - 1)],
          next = points[Math.min(points.length - 1, index + 1)];
        const before = Math.hypot(point.x - previous.x, point.y - previous.y);
        const after = Math.hypot(next.x - point.x, next.y - point.y);
        const total = before + after || 1;
        return {
          previous: {
            x: Math.max(0, Math.min(1000, point.x - (next.x - previous.x) * tension * before / total)),
            y: Math.max(0, Math.min(100, point.y - (next.y - previous.y) * tension * before / total))
          },
          next: {
            x: Math.max(0, Math.min(1000, point.x + (next.x - previous.x) * tension * after / total)),
            y: Math.max(0, Math.min(100, point.y + (next.y - previous.y) * tension * after / total))
          }
        };
      });
      let path = 'M' + points[0].x.toFixed(1) + ' ' + points[0].y.toFixed(2);
      for (let index = 1; index < points.length; index++) {
        const c1 = controls[index - 1].next,
          c2 = controls[index].previous,
          point = points[index];
        path += ' C' + c1.x.toFixed(1) + ' ' + c1.y.toFixed(2) + ' ' + c2.x.toFixed(1) + ' ' + c2.y.toFixed(2) + ' ' + point.x.toFixed(1) + ' ' + point.y.toFixed(2);
      }
      return path;
    };
    const oxygenDetail = detailDays?.find(detail => detail.visibility.oxygen);
    // Merge selected days before applying the same domains/ticks as daily detail.
    const detailScales = mode === '3d' ? detailMetricScales(Object.fromEntries(selM.map((key, index) => [
      key === 'ppa' ? 'ppaC' : key, rawByMetric[index].flat()
    ])), oxygenDetail?.oxygenKey) : null;
    const SM = selM.map((k, metricIndex) => {
      const scale = detailScales?.[k],
        md = scale ? { ...MD[k], u: scale.unit } : MD[k],
        raw = rawByMetric[metricIndex],
        ser = raw.map(samples => slotSeries(samples, k)),
        [lo, hi] = scale ? [scale.lo, scale.hi] : bounds(md, raw.map(samples => samples.map(sample => sample.value)));
      return {
        k,
        md,
        raw,
        ser,
        scale,
        lo,
        hi
      };
    });
    const fmt = (md, v) => v.toFixed(md.dp);
    const tab = (list, cur, field, extra) => list.map(([key, label]) => ({
      label,
      bg: cur === key ? 'rgba(46,179,238,0.16)' : 'transparent',
      color: cur === key ? '#e4e9f0' : '#8793a6',
      fw: cur === key ? 600 : 400,
      onClick: () => this.setState({
        [field]: key,
        ...(extra || {})
      })
    }));
    const mBtn = (key, md, cur, field) => ({
      label: md.l,
      dot: md.c,
      bd: cur === key ? md.c : '#1c2636',
      bg: cur === key ? 'rgba(46,179,238,0.08)' : 'transparent',
      color: cur === key ? '#e4e9f0' : '#8793a6',
      onClick: () => this.setState({
        [field]: key
      })
    });
    const colorOfDay = d => pal[cDays.indexOf(d) % pal.length];
    const hasDepthData = this.hasDepthData();
    const depthNoDataTooltip = this.props.translate?.('analytics_depth_no_measurements') || 'No hay tiempo medido ni alarmas con profundidad en el período solicitado.';
    const out = {
      ...this.drawerVals(st),
      ...this.calVals(st),
      ...(() => {
        const D = this.data,
          N = D.days.length,
          nC = D.rows.length,
          pk = st.dsPick;
        const wh = (key, set) => e => {
          const now = Date.now();
          if (now - (this._dsW || 0) < 90) return;
          this._dsW = now;
          set(e.deltaY > 0 ? 1 : -1);
          clearTimeout(this._dsRT);
          if (this.state.dsRoll !== key) this.setState({
            dsRoll: key,
            dsPick: null
          });
          this._dsRT = setTimeout(() => this.setState({
            dsRoll: null
          }), 900);
        };
        const roll = (key, labels, idx, sub) => {
          const on = st.dsRoll === key;
          return {
            rollOn: on,
            items: on ? [-2, -1, 0, 1, 2].map(o => {
              const i = idx + o,
                ok = i >= 0 && i < labels.length;
              return {
                l: ok ? labels[i] : '',
                s: ok && o === 0 ? sub(i) : '',
                c: o === 0 ? '#ffffff' : Math.abs(o) === 1 ? '#8793a6' : '#3a4558',
                fs: o === 0 ? "1.388889vh" : "1.111111vh",
                fw: o === 0 ? 700 : 500,
                h: o === 0 ? "2.777778vh" : "1.851852vh",
                bg: o === 0 ? 'rgba(232,80,20,0.14)' : 'transparent'
              };
            }) : []
          };
        };
        return {
          dsClose: () => pk && this.setState({
            dsPick: null
          }),
          dsCage: {
            ...roll('c', D.rows.map(x => x.name), st.sel, i => D.rows[i].cells[st.day] && D.rows[i].cells[st.day].ay ? 'ayuno' : ''),
            v: D.rows[st.sel] ? D.rows[st.sel].name : '–',
            w: "4.375vw",
            open: pk === 'c',
            bd: pk === 'c' ? 'oklch(0.62 0.18 42)' : 'rgba(184,193,206,0.25)',
            toggle: () => this.setState({
              dsPick: pk === 'c' ? null : 'c'
            }),
            wheel: wh('c', s => this.setState(x => ({
              sel: (x.sel + s + nC) % nC
            }))),
            opts: D.rows.map((rw, ci) => ({
              l: rw.name,
              s: rw.cells[st.day] && rw.cells[st.day].ay ? 'ayuno' : '',
              bg: ci === st.sel ? 'oklch(0.62 0.18 42 / 0.3)' : 'transparent',
              c: ci === st.sel ? '#fff' : '#d5dbe4',
              go: () => this.setState({
                sel: ci,
                dsPick: null
              })
            }))
          },
          dsDay: {
            ...roll('d', D.days.map(d => d), st.day, i => D.rows[st.sel] && D.rows[st.sel].cells[i].ay ? 'ayuno' : ''),
            v: D.days[st.day] || '–',
            w: "6.145833vw",
            open: pk === 'd',
            bd: pk === 'd' ? 'oklch(0.62 0.18 42)' : 'rgba(184,193,206,0.25)',
            toggle: () => this.setState({
              dsPick: pk === 'd' ? null : 'd'
            }),
            wheel: wh('d', s => this.setState(x => ({
              day: Math.max(0, Math.min(N - 1, x.day + s))
            }))),
            opts: D.days.map((d, i) => ({
              l: d,
              s: D.rows[st.sel] && D.rows[st.sel].cells[i].ay ? 'ayuno' : '',
              bg: i === st.day ? 'oklch(0.62 0.18 42 / 0.3)' : 'transparent',
              c: i === st.day ? '#fff' : '#d5dbe4',
              go: () => this.setState({
                day: i,
                dsPick: null
              })
            })).reverse()
          }
        };
      })(),
      ...(st.showRng ? this.rngVals(st) : {
        showRng: false
      }),
      vsHdr: st.view === 'vs',
      vsNoHdr: st.view !== 'vs',
      navItems: [['panel', 'General', 'Panel de jaulas', 'M4 4h7v7H4zM13 4h7v4h-7zM13 10h7v10h-7zM4 13h7v7H4z'], ['cmp', 'Comparar días', 'Superponer, mapa de calor y 3D', 'M3 17l5-6 4 4 6-8 3 3M3 21h18'], ['dep', 'Profundidad', 'Perfil por estrato del winche', 'M12 3v15M7 13l5 5 5-5M4 21h16'], ['fs', 'Alimentación', 'Score, atención y reacción', 'M4 20V10M10 20V4M16 20v-7M22 20H2']].map(([k, label, title, icon]) => {
        const on = (st.view || 'panel') === k || k === 'panel' && !['cmp', 'dep', 'fs'].includes(st.view);
        return {
          label,
          title,
          icon,
          bg: on ? 'rgba(46,179,238,0.14)' : 'transparent',
          bd: on ? 'rgba(46,179,238,0.35)' : 'transparent',
          color: on ? '#e4e9f0' : '#8793a6',
          fw: on ? 600 : 400,
          bar: on ? 'oklch(0.66 0.19 40)' : 'transparent',
          onClick: () => this.setState({
            view: k,
            dpHov: null,
            fsHov: null
          })
        };
      }),
      viewTabs: tab([['panel', 'Panel'], ['cmp', 'Comparar días'], ['dep', 'Profundidad'], ['fs', 'Alimentación']], st.view, 'view'),
      viewSimple: !!st.simple && (!st.view || st.view === 'panel'),
      ...(st.simple && (!st.view || st.view === 'panel') ? this.smVals(st) : {}),
      viewCz: st.view === 'cz',
      ...(st.view === 'cz' ? this.czVals(st) : {}),
      goCz: () => this.setState({
        view: 'cz',
        czScope: 'one',
        czHov: null
      }),
      ...(st.czRule && st.view !== 'cz' ? (() => {
        const s = this.czSpans(st.sel, st.day, st.czRule);
        return {
          czRuleOn: true,
          czRuleT: st.czRule.txt + ' · ' + s.n * 5 + ' min hoy',
          czSpan: s.d,
          czRuleOff: () => this.setState({
            czRule: null
          })
        };
      })() : {
        czRuleOn: false,
        czSpan: 'M0 0'
      }),
      viewPanel: !['cmp', 'fs', 'vs', 'cz'].includes(st.view) && !(st.simple && (!st.view || st.view === 'panel')),
      viewVs: st.view === 'vs',
      ...(st.view === 'vs' ? this.vsVals(st) : {}),
      detNormal: st.view !== 'dep',
      backDet: () => this.setState({
        view: 'panel',
        dpHov: null
      }),
      viewFs: st.view === 'fs',
      goFs: () => this.setState({
        view: 'fs',
        fsHov: null
      }),
      ...(() => {
        const SD = this.scoreData(),
          v = SD.M[ci].filter(Boolean),
          s = v.length ? v.reduce((a, c) => a + c.score, 0) / v.length : null;
        return {
          hdrSc: s == null ? '–' : s.toFixed(1),
          hdrScC: s == null ? '#8793a6' : this.scoreC(s, SD.flat)
        };
      })(),
      ...(st.view === 'fs' ? this.fsVals(st) : {}),
      viewCmp: st.view === 'cmp',
      viewDep: st.view === 'dep',
      depthDisabled: !hasDepthData,
      depthTooltip: hasDepthData ? '' : depthNoDataTooltip,
      goDep: !hasDepthData ? undefined : () => this.setState({
        view: 'dep',
        dpHov: null
      }),
      ...(st.view === 'dep' ? this.depVals(st) : {}),
      goCmp: () => this.setState({
        view: 'cmp',
        cDays: null,
        cEnd: null
      }),
      cModes: tab([['sup', 'Superpuesto'], ['map', 'Mapa de calor'], ['3d', '3D']], mode, 'cMode'),
      cIsSup: mode === 'sup',
      cNotSup: mode !== 'sup',
      cIs3d: mode === '3d',
      cMetricTitle: 'Métricas',
      cMetrics: Object.keys(MD).map(k => {
        const on = selM.includes(k),
          md = MD[k];
        return {
          key: k,
          label: md.l,
          dot: on ? md.c : '#3a475c',
          bd: on ? md.c : '#1c2636',
          bg: on ? 'rgba(46,179,238,0.08)' : 'transparent',
          color: on ? '#e4e9f0' : '#8793a6',
          on,
          onClick: () => this.setState({
            cMetrics: on ? selM.length > 1 ? selM.filter(x => x !== k) : selM : Object.keys(MD).filter(x => selM.includes(x) || x === k)
          })
        };
      }),
      cMetricMore: st.cMetricMore == null ? selM.some(k => ['oxy', 'temp', 'cur', 'dep'].includes(k)) : !!st.cMetricMore,
      cMetricToggle: () => this.setState({
        cMetricMore: !(st.cMetricMore == null ? selM.some(k => ['oxy', 'temp', 'cur', 'dep'].includes(k)) : !!st.cMetricMore)
      }),
      cTitle: mode === 'sup' ? 'Superpuesto · ' + cDays.length + ' días' : mode === 'map' ? 'Mapa de calor · ' + cDays.length + ' días' : 'Superficie 3D · ' + cDays.length + ' días',
      cSub: mode === 'sup' ? 'Un gráfico por métrica · mismo eje horario · un color por día' : mode === 'map' ? 'Días seleccionados · cada fila es un día · intensidad = valor' : 'X = hora · Y = día · altura = valor (escala propia de cada métrica)',
      cCalHint: '',
      ctD: 'none',
      ctRows: [],
      cFocOn: false,
      ctFoot: '',
      cHours: cmpHours,
      cDown: this.cDown,
      cDragH: this.cDragH,
      cUp: this.cUp,
      cOut: this.cOut,
      cWheel: this.cWheel,
      cNoMenu: e => e.preventDefault()
    };
    out.cMetricMain = out.cMetrics.filter(metric => ['act', 'tasa', 'ppa'].includes(metric.key));
    out.cMetricExtra = out.cMetrics.filter(metric => ['oxy', 'temp', 'cur', 'dep'].includes(metric.key));
    out.cSelectedDays = cDays.slice().sort((a, b) => b - a).map(d => ({
      lab: D.days[d],
      color: colorOfDay(d),
      canRemove: cDays.length > 1,
      onRemove: () => cDays.length > 1 && this.setState({
        cDays: cDays.filter(day => day !== d)
      })
    }));
    const toggleDay = d => {
      if (!Number.isInteger(d) || d < 0 || d >= N) return;
      if (cDays.includes(d)) {
        if (cDays.length > 1) this.setState({
          cDays: cDays.filter(day => day !== d)
        });
        return;
      }
      if (cDays.length < 6) this.setState({
        cDays: [...cDays, d]
      });
    };
    out.cDayPicker = {
      open: !!st.cDayPick,
      value: cDays.length + ' de 6',
      bd: st.cDayPick ? 'oklch(0.62 0.18 42)' : 'rgba(184,193,206,0.25)',
      toggle: () => this.setState({
        cDayPick: !st.cDayPick
      }),
      close: () => st.cDayPick && this.setState({
        cDayPick: false
      })
    };
    out.cDayOptions = D.days.map((lab, d) => {
      const selected = cDays.includes(d);
      return {
        d,
        lab,
        selected,
        disabled: !selected && cDays.length >= 6,
        bg: selected ? 'oklch(0.62 0.18 42 / 0.3)' : 'transparent',
        color: selected ? '#fff' : '#d5dbe4',
        mark: selected ? '✓' : '',
        onClick: () => toggleDay(d)
      };
    }).reverse();
    out.cDayAdd = event => toggleDay(Number(event.target.value));
    const recentDays = count => {
      const start = Math.max(0, N - count);
      return Array.from({
        length: N - start
      }, (_, index) => start + index);
    };
    const latestDay = Math.max(0, N - 1);
    const previousWeek = [Math.max(0, latestDay - 7), latestDay].filter((d, index, days) => days.indexOf(d) === index);
    out.cDayPresets = [{
      label: '2 recientes',
      onClick: () => this.setState({
        cDays: recentDays(2),
        cDayPick: false
      })
    }, {
      label: '3 recientes',
      onClick: () => this.setState({
        cDays: recentDays(3),
        cDayPick: false
      })
    }, 
    // {
    //   label: 'Semana anterior',
    //   onClick: () => this.setState({
    //     cDays: previousWeek,
    //     cDayPick: false
    //   })
    // }
  ];
    if (mode === 'sup') {
      const n = SM[0].ser[0].length,
        X = i => (i / Math.max(1, n - 1) * 1000).toFixed(1);
      const hi_ = st.cHover,
        on = hi_ != null && hi_ < n,
        nm = SM.length,
        ph = Math.max(60, Math.floor((470 - 8 * (nm - 1)) / nm));
      out.cPanels = SM.map(s => {
        const Y = v => (100 - (v - s.lo) / (s.hi - s.lo) * 100).toFixed(2);
        return {
          label: s.md.l + ' [' + s.md.u + ']',
          c: s.md.c,
          h: ph,
          yt: [0, 0.5, 1].map(t => ({
            pos: t * 100 + '%',
            lab: fmt(s.md, s.lo + (s.hi - s.lo) * t)
          })),
          lines: cDays.map((d, j) => ({
            // Same timestamps and curved interpolation used by the V1 Chart.js dataset.
            d: curvePath(s.raw[j], s.k, t => (t - cmpStart) / (cmpEnd - cmpStart) * 1000, Y),
            c: colorOfDay(d),
            w: d === st.day ? 2.6 : 1.8
          })),
          tip: on ? cDays.map((d, j) => ({
            lab: D.days[d],
            c: colorOfDay(d),
            v: s.ser[j][hi_]
          })).sort((a, b) => b.v - a.v).map(r => ({
            ...r,
            val: fmt(s.md, r.v) + ' ' + s.md.u
          })) : []
        };
      });
      out.cLeg = cDays.map(d => ({
        lab: D.days[d],
        c: colorOfDay(d),
        onRemove: () => this.setState({
          cDays: cDays.length > 1 ? cDays.filter(x => x !== d) : cDays
        })
      }));
      out.cMove = e => {
        const r = e.currentTarget.getBoundingClientRect();
        const i = Math.round(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * (n - 1));
        if (i !== this.state.cHover) this.setState({
          cHover: i
        });
      };
      out.cLeave = () => this.setState({
        cHover: null
      });
      out.cHd = on ? 'block' : 'none';
      out.cHdf = on ? 'flex' : 'none';
      out.cHx = on ? (hi_ / Math.max(1, n - 1) * 100).toFixed(2) + '%' : '0%';
      out.cTx = on && hi_ / Math.max(1, n - 1) > 0.7 ? "translateX(calc(-100% - 1.111111vh))" : "translateX(1.111111vh)";
      out.cTipT = on ? hm(cmpStart + hi_ * 5) : '';
      out.cRows = cDays.map(d => {
        const det = this.getDet(ci, d),
          P = det.pts,
          feed = P.filter(p => p.tasa > 0),
          A = det.alarms,
          na = A.filter(a => !a.att).length;
        return {
          date: D.days[d],
          c: colorOfDay(d),
          al: A.length + (na ? ' (' + na + ' sin atender)' : ''),
          alC: na ? 'oklch(0.72 0.17 25)' : '#e4e9f0',
          ppa: String(Math.round(P.reduce((a, p) => a + (Number.isFinite(p.ppa) ? p.ppa : 0), 0))),
          act: (v => v.length ? (v.reduce((a, b) => a + b, 0) / v.length).toFixed(0) + ' %' : feed.length ? '—' : 'ayuno')(feed.map(p => p.act).filter(Number.isFinite)),
          cur: (v => v.length ? Math.max(...v).toFixed(1) + ' cm/s' : '—')(P.map(p => p.cur).filter(Number.isFinite)),
          oxy: (v => v.length ? Math.min(...v).toFixed(1) + ' %' : '—')(P.map(p => p.oxy).filter(Number.isFinite)),
          temp: (v => v.length ? (v.reduce((a, b) => a + b, 0) / v.length).toFixed(2) + ' °C' : '—')(P.map(p => p.temp).filter(Number.isFinite)),
          open: () => this.setState({
            view: 'panel',
            exp: null,
            day: d
          })
        };
      });
      this._cmp = {
        mode: 'sup'
      };
    } else {
      // Keep the same five-minute presentation resolution used by the V1 curve.
      // Internal gaps were already interpolated above; no extra downsampling here.
      const pick = arr => arr.slice();
      clearTimeout(this._dt);
      this._dt = setTimeout(() => this.draw(), 30);
      this._cmp = {
        mode,
        hours: cmpHours,
        days: range.map(d => D.days[d]),
        dIdx: range,
        selRow: range.indexOf(st.day),
        metrics: SM.map(s => {
          const step = Math.abs(s.hi - s.lo) / 4,
            digits = Math.min(6, Math.max(s.md.dp, 2 - Math.floor(Math.log10(step))));
          return {
            key: s.k,
            step: s.k === 'tasa',
            rgb: HEX[s.k],
            name: s.md.l + ' [' + s.md.u + ']',
            min: s.lo,
            max: s.hi,
            ticks: s.scale ? [...new Set(s.scale.values)].map(value => ({
              f: (value - s.lo) / (s.hi - s.lo),
              value,
              lab: value.toFixed(s.scale.digits)
            })).sort((a, b) => a.f - b.f) : Array.from({ length: 5 }, (_, index) => {
              const f = index / 4,
                value = s.lo + (s.hi - s.lo) * f;
              return { f, value, lab: value.toLocaleString('es-CL', { maximumFractionDigits: digits }) };
            }),
            raw: s.ser.map(pick),
            z: s.ser.map(r => pick(r).map(v => Number.isFinite(v) ? Math.max(0, Math.min(1, (v - s.lo) / (s.hi - s.lo))) : 0))
          };
        })
      };
      const t = st.cTipC;
      if (t && t.r < range.length && SM[t.k]) {
        const dd = range[t.r],
          mins = cmpStart + t.b * 5,
          sec = mins * 60,
          A = this.getDet(ci, dd).alarms,
          act = A.filter(a => a.sec <= sec + 450 && a.sec + a.dur >= sec - 450);
        out.ctD = 'flex';
        out.ctX = (t.px * 100).toFixed(2) + '%';
        out.ctY = (t.py * 100).toFixed(2) + '%';
        if (mode === 'map') {
          out.ctY = Math.max(12, Math.min(82, t.py * 100)).toFixed(2) + '%';
          out.ctTx = 'translate(' + (t.px > 0.6 ? "calc(-100% - 1.666667vh)" : "1.666667vh") + ',-50%)';
        } else {
          const right = t.px < 0.5;
          out.ctX = right ? "calc(100% - 0.740741vh)" : "0.740741vh";
          out.ctY = Math.max(10, Math.min(80, t.py * 100)).toFixed(2) + '%';
          out.ctTx = 'translate(' + (right ? '-100%' : '0') + ',-50%)';
        }
        out.ctBd = SM[t.k].md.c;
        out.ctT = D.days[dd] + ' · ' + hm(mins);
        out.ctAl = act.length ? act.length + (act.length > 1 ? ' alarmas' : ' alarma') + ' en ±7 min' : 'Sin alarmas cerca';
        out.ctAlC = act.length ? 'oklch(0.72 0.17 25)' : '#5a667a';
        out.ctRows = SM.map((s, k) => ({
          lab: s.md.l,
          c: s.md.c,
          val: fmt(s.md, this._cmp.metrics[k].raw[t.r][t.b]) + ' ' + s.md.u,
          fw: k === t.k ? 600 : 400,
          lc: k === t.k ? '#e4e9f0' : '#b8c1ce'
        }));
      } else {
        out.ctD = 'none';
        out.ctX = '0%';
        out.ctY = '0%';
        out.ctTx = 'none';
        out.ctBd = '#2a3446';
        out.ctT = '';
        out.ctAl = '';
        out.ctAlC = '#5a667a';
        out.ctRows = [];
      }
      {
        const fd = st.cFocus,
          fi = fd == null ? -1 : range.indexOf(fd);
        out.cFocOn = fi >= 0;
        out.ctFoot = 'Clic = enfocar este día · clic en vacío = quitar';
        if (fi >= 0) {
          const det = this.getDet(ci, fd),
            P = det.pts,
            A = det.alarms;
          out.cFocLab = D.days[fd];
          out.cFocSum = (D.rows[ci].cells[fd].ay ? 'Ayuno · ' : '') + A.length + ' alarmas · PPA ' + Math.round(P[P.length - 1].ppaC) + ' · corriente máx. ' + Math.max(...P.map(p => p.cur)).toFixed(1) + ' cm/s';
          out.cFocPrev = () => this.setState({
            cFocus: range[(fi - 1 + range.length) % range.length]
          });
          out.cFocNext = () => this.setState({
            cFocus: range[(fi + 1) % range.length]
          });
          out.cFocOpen = () => this.setState({
            view: 'panel',
            day: fd,
            cFocus: null
          });
          out.cFocClear = () => this.setState({
            cFocus: null
          });
        }
      }
      out.cDaysN = range.length + ' días';
      out.cMLeg = SM.map(s => ({
        c: s.md.c,
        lab: s.md.l,
        range: fmt(s.md, s.lo) + '–' + fmt(s.md, s.hi) + ' ' + s.md.u
      }));
      out.cHint = '';
      out.cCursor = mode === '3d' ? 'grab' : 'default';
      out.cReset = () => {
        this._yaw = -0.55;
        this._pitch = 0.8;
        this._zoom = 1;
        this._panX = 0;
        this._panY = 0;
        this.setState({
          cZoom: 1
        });
        this.draw();
      };
      out.cZoomIn = this.cZoomBy(1.25);
      out.cZoomOut = this.cZoomBy(0.8);
      out.cZoomTxt = Math.round((this._zoom ?? 1) * 100) + ' %';
    }
    return out;
  }
};
export default logic;
